import React from 'react';
import { useLocation } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { environmentConfig } from '../config/environment';
import { Sidebar } from '../components';
import PartnerSidebar from '../partner/components/sidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { usePageTitleManager } from '../hooks/usePageTitleManager';
import { useIsMobile } from '../hooks/useMobile';
import { useScrollBlock } from '../hooks/useScrollBlock';
import { AnimatePresence, LazyMotion, domAnimation, m } from 'framer-motion';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = React.memo(({ children }) => {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const isMobile = useIsMobile();
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [blockScroll, allowScroll] = useScrollBlock();

  // Автоматическое управление тайтлами страниц
  usePageTitleManager();

  const isPublicPartnerLinkPage = location.pathname.startsWith('/partner/public');
  const isPartner = user?.role === 'partner';
  const SidebarComponent = isPartner ? PartnerSidebar : Sidebar;
  const handleCloseSidebar = React.useCallback(() => setIsSidebarOpen(false), []);

  React.useEffect(() => {
    if (!isMobile) {
      setIsSidebarOpen(false);
    }
  }, [isMobile]);

  React.useEffect(() => {
    if (!isAuthenticated) {
      setIsSidebarOpen(false);
      allowScroll();
    }
  }, [allowScroll, isAuthenticated]);

  React.useEffect(() => {
    if (isSidebarOpen && isMobile) {
      blockScroll();
      return () => allowScroll();
    }
    allowScroll();
  }, [allowScroll, blockScroll, isMobile, isSidebarOpen]);

  if (isPublicPartnerLinkPage) {
    return <div className="min-h-screen">{children}</div>;
  }

  // Don't show sidebar for login pages
  if (!isAuthenticated) {
    return <div className="h-screen">{children}</div>;
  }

  // Determine which sidebar to show based on user role
  // Determine which sidebar to show based on user role
  // Show sidebar for authenticated users
  return (
    <div className="flex md:flex-row flex-col max-md:min-h-screen md:h-screen">
      {!isMobile && (
        <div className="h-screen overflow-y-auto overflow-x-hidden">
          <SidebarComponent />
        </div>
      )}
      <div className="flex flex-1 flex-col overflow-hidden md:p-0 pt-[5.25rem]">
        <Header onBurgerClick={() => setIsSidebarOpen(true)} />
        <div className="flex flex-1 flex-col md:overflow-auto">
          <div className="flex-1">{children}</div>
          <Footer isPartner={isPartner} />
        </div>
      </div>
      <LazyMotion features={domAnimation}>
        <AnimatePresence>
          {isMobile && isSidebarOpen && (
            <m.div
              className="fixed inset-0 z-50 flex flex-col"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <m.aside
                className="relative h-full w-full bg-white"
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'tween', duration: 0.25 }}
              >
                <div className="size-full overflow-y-auto">
                  <SidebarComponent onItemClick={handleCloseSidebar} onClose={handleCloseSidebar} />
                </div>
              </m.aside>
            </m.div>
          )}
        </AnimatePresence>
      </LazyMotion>
    </div>
  );
});

AppLayout.displayName = 'AppLayout';

export default AppLayout;
